// Экран 5 из ТЗ раздел 3.3 — НАМЕРЕННО только апгрейд (переход на тариф дороже за оставшийся срок),
// см. app/api/routes/miniapp/change_plan.py. Даунгрейд не поддерживается: кнопка есть на любой
// другой активный нетриальный небандловый тариф, но backend отклонит попытку понятным сообщением,
// если это не апгрейд — дублировать проверку "дороже ли за остаток срока" на фронте не стали,
// чтобы не рассинхронизировать формулу с compute_upgrade_amount. Бандл-тарифы (сейчас только
// "Расширенный", plan.is_bundle) исключены и как источник, и как цель — апгрейд между двумя
// подписками разных тарифных групп не поддерживается вообще.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, changePlan, fetchPlans, type Plan } from "../api/client";
import { openInvoice } from "../api/openInvoice";
import { useSubscription } from "../hooks/useSubscription";

export function ChangePlanScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscription, loading: subscriptionLoading, error: subscriptionError } = useSubscription();
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyPlanId, setBusyPlanId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPlans()
      .then((data) => {
        if (!cancelled) setPlans(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (planId: number) => {
    setBusyPlanId(planId);
    setError(null);
    changePlan(planId)
      .then(({ invoice_link }) => {
        openInvoice(invoice_link, (status) => {
          setBusyPlanId(null);
          if (status === "paid") {
            navigate("/subscription");
          } else if (status !== "cancelled") {
            setError(t("change_plan_payment_failed"));
          }
        });
      })
      .catch((err: unknown) => {
        setBusyPlanId(null);
        setError(err instanceof ApiError ? err.message : String(err));
      });
  };

  const loading = subscriptionLoading || plans === null;
  const currentIsBundle = subscription?.plan.is_bundle ?? false;
  const candidates =
    plans?.filter(
      (plan) => !plan.is_trial && !plan.is_bundle && plan.id !== subscription?.plan.id,
    ) ?? [];

  return (
    <div>
      <h1>{t("change_plan_title")}</h1>
      {(error || subscriptionError) && <p role="alert">{error ?? subscriptionError}</p>}
      {loading && <p>{t("loading")}</p>}
      {!loading && subscription === null && <p>{t("subscription_none")}</p>}
      {!loading && subscription && (
        <div>
          <p>
            {t("change_plan_current")}: <strong>{subscription.plan.title}</strong>
          </p>
          {currentIsBundle ? (
            <p>{t("change_plan_bundle_unavailable")}</p>
          ) : (
            <>
              <p>{t("change_plan_note")}</p>
              {candidates.length === 0 && <p>{t("change_plan_no_candidates")}</p>}
              <ul>
                {candidates.map((plan) => (
                  <li key={plan.id}>
                    <strong>{plan.title}</strong> — {plan.price} {plan.currency} /{" "}
                    {plan.duration_days} {t("days_short")}{" "}
                    <button
                      type="button"
                      onClick={() => handleChange(plan.id)}
                      disabled={busyPlanId === plan.id}
                    >
                      {busyPlanId === plan.id ? t("loading") : t("change_plan_switch")}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
